import { Center, Flex, Spinner, Text } from '@chakra-ui/react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { SyncInitialPreferencesToFilters } from '../../shared/user-preferences/SyncInitialPreferencesToFilters';
import type { RevelioPageId } from '../CustomDash/components/PageConfigProvider/types';
import { GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT } from '../CustomDash/constants/panelWidths';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import { FilterProvider } from '../CustomDash/filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../CustomDash/filters/types/filters';
import type { GlobalAiChartPreviewRuntimeSnapshot } from './global-ai-assistant-chart-preview-runtime';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';

function ApplyAiSearchFiltersOnce({
  messageId,
  filters,
  primarySelectionMergeMode,
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
  compactLoading = false,
  onApplyResult,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  primarySelectionMergeMode: 'merge' | 'replace';
  loadingMinHeight?: string;
  compactLoading?: boolean;
  /**
   * Headless mode: report the apply outcome here INSTEAD of the GA
   * chart-preview runtime (which is keyed by transcript message ids the
   * headless digest host doesn't have).
   */
  onApplyResult?: (result: { ok: boolean; error?: string }) => void;
  children: ReactNode;
}) {
  const ga = useGlobalAIAssistantOptional();
  const mergeRuntimeRef = useRef<
    | ((
        id: string,
        patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
      ) => void)
    | undefined
  >(undefined);
  mergeRuntimeRef.current = ga?.mergeChartPreviewRuntime;
  const addTimelineEventRef = useRef<
    ((messageId: string, event: string) => void) | undefined
  >(undefined);
  addTimelineEventRef.current = ga?.addChartPreviewTimelineEvent;

  const { applyAiSearchFilters } = useApplyAiSearchFilters();
  const [filtersReady, setFiltersReady] = useState(filters === undefined);
  const applyRef = useRef(applyAiSearchFilters);
  applyRef.current = applyAiSearchFilters;
  const onApplyResultRef = useRef(onApplyResult);
  onApplyResultRef.current = onApplyResult;

  const filtersKey =
    filters === undefined ? '__none__' : JSON.stringify(filters);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useLayoutEffect(() => {
    const filtersNow = filtersRef.current;
    const merge = mergeRuntimeRef.current;
    // Headless mode: the outcome callback replaces the message-id-keyed
    // runtime reporting wholesale (there is no transcript message).
    const reportResult = onApplyResultRef.current;
    if (filtersNow === undefined) {
      if (reportResult) {
        reportResult({ ok: true });
      } else {
        merge?.(messageId, {
          phase: 'filters_ready',
          filterApplyError: undefined,
        });
        addTimelineEventRef.current?.(messageId, 'filters skipped (none)');
      }
      setFiltersReady(true);
      return;
    }

    if (!reportResult) {
      merge?.(messageId, {
        phase: 'applying_filters',
        filterApplyError: undefined,
      });
      addTimelineEventRef.current?.(messageId, 'applying filters');
    }
    setFiltersReady(false);

    let cancelled = false;
    void (async () => {
      try {
        await applyRef.current(filtersNow, {
          primarySelectionMergeMode,
          skipTalentDiscoveryAdaptFallback: true,
          // Headless data fetch: role ids MUST be queried under the taxonomy
          // they were resolved with, or the backend rejects/mismatches them.
          // Deliberately scoped to this isolated preview scope — on real pages
          // the same pairing would visibly switch the page's taxonomy
          // selection, a product decision not yet made.
          pairRoleTaxonomyWithResponse: true,
        });
        if (!cancelled) {
          if (onApplyResultRef.current) {
            onApplyResultRef.current({ ok: true });
          } else {
            mergeRuntimeRef.current?.(messageId, {
              phase: 'filters_ready',
              filterApplyError: undefined,
            });
            addTimelineEventRef.current?.(messageId, 'ai filters applied');
          }
        }
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : 'Failed to apply AI filters';
        if (!cancelled) {
          if (onApplyResultRef.current) {
            onApplyResultRef.current({ ok: false, error: msg });
          } else {
            mergeRuntimeRef.current?.(messageId, {
              phase: 'error',
              filterApplyError: msg,
            });
            addTimelineEventRef.current?.(
              messageId,
              `ai filters failed: ${msg}`
            );
          }
        }
      } finally {
        if (!cancelled) {
          setFiltersReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filtersKey, messageId, primarySelectionMergeMode]);

  if (!filtersReady) {
    if (compactLoading) {
      return (
        <Flex align="center" gap={2} py={1}>
          <Spinner size="sm" color="#2D426A" />
          <Text fontSize="12px" lineHeight="15px" color="#2D426A" opacity={0.8}>
            Applying filters…
          </Text>
        </Flex>
      );
    }
    return (
      <Center minH={loadingMinHeight} w="100%" flexShrink={0}>
        <Spinner color="green.500" />
      </Center>
    );
  }

  return children;
}

/**
 * Isolated filter state for a single global-assistant chart preview.
 * Does not read or write URL filters; applies the AI search snapshot once per message.
 */
export function GlobalAIAssistantChartFilterScope({
  messageId,
  filters,
  pageId,
  revelioPageId,
  primaryFilterId,
  primaryFilterSelectionLimit,
  primaryFilterSelectionDefault,
  primarySelectionMergeMode = 'replace',
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
  compactLoading = false,
  onApplyResult,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  pageId: string;
  revelioPageId?: RevelioPageId;
  primaryFilterId?: PrimaryFilterId;
  primaryFilterSelectionLimit?: number;
  primaryFilterSelectionDefault?: number;
  /**
   * `replace`: do not union AI primary entities with template defaults (single-bar previews).
   * @default 'replace'
   */
  primarySelectionMergeMode?: 'merge' | 'replace';
  loadingMinHeight?: string;
  compactLoading?: boolean;
  /**
   * Headless mode (digest host): receive the AI filter-apply outcome instead
   * of reporting it to the chart-preview runtime. See ApplyAiSearchFiltersOnce.
   */
  onApplyResult?: (result: { ok: boolean; error?: string }) => void;
  children: ReactNode;
}) {
  return (
    <FilterProvider
      key={messageId}
      pageId={pageId}
      revelioPageId={revelioPageId}
      primaryFilterId={primaryFilterId}
      primaryFilterSelectionLimit={primaryFilterSelectionLimit}
      primaryFilterSelectionDefault={primaryFilterSelectionDefault}
      hydrateFromUrl={false}
      syncFiltersToUrl={false}
      persistScope={false}
    >
      {/* skipTaxonomies: this scope's taxonomy pairing comes from the AI response;
          pref-sync's first-mount passive effect reads pre-apply filter state, so
          its isAiApplied guard can't see the apply and would clobber the pairing. */}
      <SyncInitialPreferencesToFilters
        revelioPageId={revelioPageId}
        skipTaxonomies
      />
      <ApplyAiSearchFiltersOnce
        messageId={messageId}
        filters={filters}
        primarySelectionMergeMode={primarySelectionMergeMode}
        loadingMinHeight={loadingMinHeight}
        compactLoading={compactLoading}
        onApplyResult={onApplyResult}
      >
        {children}
      </ApplyAiSearchFiltersOnce>
    </FilterProvider>
  );
}
